import { createContext, useEffect, useReducer, useRef } from 'react';
import PropTypes from 'prop-types';
import { authApi } from '../api/auth-api';

var ActionType;
(function (ActionType) {
  ActionType['INITIALIZE'] = 'INITIALIZE';
  ActionType['LOGIN']      = 'LOGIN';
  ActionType['LOGOUT']     = 'LOGOUT';
  ActionType['REGISTER']   = 'REGISTER';
})(ActionType || (ActionType = {}));

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return { ...state, isAuthenticated, isInitialized: true, user };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;
    return { ...state, isAuthenticated: true, user };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;
    return { ...state, isAuthenticated: true, user };
  }
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

export const AuthContext = createContext({
  ...initialState,
  platform: 'JWT',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve()
});

export const AuthProvider = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  // Lit le tenant depuis l'URL UNE SEULE FOIS au montage.
  // On utilise useRef pour éviter tout re-render lié au routeur :
  // useNavigate() et useParams() font re-render AuthProvider à chaque
  // navigation et risquent de relancer initialize() inutilement.
  const tenantRef = useRef(window.location.pathname.split('/')[1] || '');

  const initialize = async () => {
    const currentTenant = window.location.pathname.split('/')[1] || '';

    // Met à jour le localStorage uniquement si le tenant a changé
    if (currentTenant !== globalThis.localStorage.getItem('tenant')) {
      localStorage.setItem('tenant', currentTenant);
    }
    tenantRef.current = currentTenant;

    try {
      const accessToken = globalThis.localStorage.getItem(`accessToken_${currentTenant}`);
      if (accessToken) {
        const user = await authApi.me(accessToken);
        dispatch({
          type: ActionType.INITIALIZE,
          payload: { isAuthenticated: true, user }
        });
      } else {
        dispatch({
          type: ActionType.INITIALIZE,
          payload: { isAuthenticated: false, user: null }
        });
      }
    } catch (err) {
      console.error(err);
      dispatch({
        type: ActionType.INITIALIZE,
        payload: { isAuthenticated: false, user: null }
      });
    }
  };

  // S'exécute UNE SEULE FOIS au montage du composant.
  // La navigation SPA ne recrée jamais AuthProvider, donc pas besoin
  // de re-initialiser lors des changements de route.
  useEffect(() => {
    initialize();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (email, password) => {
    const accessToken = await authApi.login(email, password);
    if (accessToken.jwt) {
      const tenant = globalThis.localStorage.getItem('tenant');
      const user = await authApi.me(accessToken.jwt);
      localStorage.setItem(`accessToken_${tenant}`, accessToken.jwt);
      dispatch({ type: ActionType.LOGIN, payload: { user } });
      return { done: true, first_login: accessToken.first_login, new_device: accessToken.new_device };
    }
    dispatch({ type: ActionType.LOGIN, payload: { isAuthenticated: false, user: null } });
    return false;
  };

  const logout = async () => {
    const tenant = globalThis.localStorage.getItem('tenant');
    localStorage.removeItem(`accessToken_${tenant}`);
    dispatch({ type: ActionType.LOGOUT });
  };

  const register = async (email, name, password) => {
    const accessToken = await authApi.register({ email, name, password });
    const user = await authApi.me(accessToken);
    const tenant = globalThis.localStorage.getItem('tenant');
    localStorage.setItem(`accessToken_${tenant}`, accessToken);
    dispatch({ type: ActionType.REGISTER, payload: { user } });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: 'JWT',
        login,
        logout,
        register
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const AuthConsumer = AuthContext.Consumer;
